import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, IconButton, List, ListItem, ListItemText, Paper, Avatar, Drawer, Icon, Button } from '@mui/material';
import { DrawerHeader } from '../drawer/header.drawer';
import { useSignalR } from './chat.signalr.context';
import { handleErrorResponse } from '../../_helpers/handleResponse.helper';
import { chatService } from '../../_services/chat.service';
import { formatTime } from '../../_helpers/dateTime.helper';
import EmojiPicker from 'emoji-picker-react';
import { IFilteringViewModel } from '../../_common/interface/table';

type Message = {
    fromUserId: any;
    toUserId: any;
    message: any;
    timestamp: any;
    groupId?: any;
};

export function ChatWindow(params: { currentUser: any, recipient?: any, group?: any, users: any[], onClose: () => void }) {
    const { currentUser, recipient, group, users, onClose } = params;
    const [history, setHistory] = useState<Message[]>([]);
    const [liveMessages, setLiveMessages] = useState<Message[]>([]);
    const { connection, incomingMessages, setIncomingMessages } = useSignalR();

    const [input, setInput] = useState('');
    const bottomRef = useRef<HTMLDivElement | null>(null);

    const [showEmoji, setShowEmoji] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const recipientRef = useRef(recipient);
    const groupRef = useRef(group);

    //Msg History Pagination
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true); //set false when backend returns < page size
    const listRef = useRef<HTMLUListElement | null>(null);
    const [filter, setFilter] = useState<IFilteringViewModel>({
        id: 0,
        pageSize: 100,
    });

    const allMessages = [...history, ...liveMessages];

    //Resolve a userId to a display name
    const getUserName = (id: any) => {
        if (id === currentUser.id) return 'You';
        const found = users.find((u: any) => u.id === id);
        return found ? found.name : id;
    };

    //Scroll the list to the very bottom
    const scrollToBottom = () => {
        requestAnimationFrame(() => {
            const el = listRef.current;
            if (!el) return;
            el.scrollTop = el.scrollHeight;
        });
    };

    //Preserve the user visual position in the chat
    const keepChatPosition = () => {
        const container = listRef.current;
        const currentHeight = container?.scrollHeight ?? 0;

        requestAnimationFrame(() => {
            if (!container) return;
            const newHeight = container.scrollHeight;
            container.scrollTop = newHeight - currentHeight;
        });
    };

    const sendMessage = async () => {
        if (connection && input.trim()) {
            if (group) {
                await connection.invoke('SendMessageToGroup', group.id, currentUser.id, input);
            } else if (recipient) {
                await connection.invoke('SendMessage', currentUser.id, recipient.id, input);
            }
            setInput('');
            setShowEmoji(false);
        }
    };

    //Load the next 100 previous messages
    const loadOlderMessages = async () => {
        if (isLoading || !hasMore) return;

        setIsLoading(true);
        try {
            const oldestMsg: any = history[0];
            filter.id = oldestMsg?.id;

            let messages: Message[] | undefined;

            if (group) {
                messages = await chatService
                    .getGroupChatHistory(group.id, currentUser.id, "", filter)
                    .catch(handleErrorResponse);
            } else if (recipient) {
                messages = await chatService
                    .getChatHistory(currentUser.id, recipient.id,"", filter)
                    .catch(handleErrorResponse);
            }

            const previousMessages = messages || [];
            setHistory(_previousMessages => [...previousMessages, ..._previousMessages]);

            //If we have less than 100 msgs, we are out of pages
            setHasMore(previousMessages.length === 100 && previousMessages.length > 0);

            //Preserve the user visual position in the chat
            keepChatPosition();
        } finally {
            setIsLoading(false);
        }
    };

    const fetchData = async () => {
        if (group) {
            return chatService.getGroupChatHistory(group.id, currentUser.id, true)
                .then(json => {
                    setHistory(json || []);
                    setLiveMessages([]);
                    setIncomingMessages((msgs: Message[]) => msgs.filter(m => m.groupId !== group.id));
                    scrollToBottom();
                }, handleErrorResponse);
        } else if (recipient) {
            return chatService.getChatHistory(currentUser.id, recipient.id, true)
                .then(json => {
                    setHistory(json || []);
                    setLiveMessages([]);
                    setIncomingMessages((msgs: Message[]) =>
                        msgs.filter(
                            msg =>
                                !(
                                    (msg.fromUserId === recipient.id && msg.toUserId === currentUser.id) ||
                                    (msg.fromUserId === currentUser.id && msg.toUserId === recipient.id)
                                )
                        )
                    );

                    scrollToBottom();
                }, handleErrorResponse);
        }
    };

    useEffect(() => {
        recipientRef.current = recipient;
    }, [recipient]);

    useEffect(() => {
        groupRef.current = group;
    }, [group]);

    useEffect(() => {
        if (connection && group) {
            connection.invoke('JoinGroup', `group-${group.id}`);
        }
    }, [connection, group]);

    //Fetch history and clear live on recipient or group change/open
    useEffect(() => {
        fetchData();
    }, [recipient, group, currentUser.id, setIncomingMessages]);

    //Listen for incoming messages while chat is open
    useEffect(() => {
        if (!connection) return;

        const dmHandler = (fromUserId: any, toUserId: any, message: any, timestamp: any, msgGroupId?: any) => {

            if (msgGroupId) return;

            const r = recipientRef.current;
            if (
                r &&
                (
                    (fromUserId === r.id && toUserId === currentUser.id) ||
                    (fromUserId === currentUser.id && toUserId === r.id)
                )
            ) {
                setLiveMessages(msgs => [
                    ...msgs,
                    { fromUserId, toUserId, message, timestamp: timestamp || new Date().toISOString(), groupId: undefined }
                ]);
            }
        };

        //Group handler � only append when the open group matches
        const groupHandler = (groupId: any, fromUserId: any, message: any, timestamp: any) => {
            const g = groupRef.current;
            if (g && groupId === g.id) {
                setLiveMessages(msgs => [
                    ...msgs,
                    { fromUserId, toUserId: null, message, timestamp: timestamp || new Date().toISOString(), groupId }
                ]);
            }
        };

        connection.on('ReceiveMessage', dmHandler);
        connection.on('ReceiveGroupMessage', groupHandler);

        return () => {
            connection.off('ReceiveMessage', dmHandler);
            connection.off('ReceiveGroupMessage', groupHandler);
        };
    }, [connection, currentUser.id]);

    //Auto-scroll -> only when not loading older, and only if user is near bottom
    useEffect(() => {
        const container = listRef.current;
        if (!container) return;

        //Don't auto-scroll during pagination of older messages
        if (isLoading) return;

        //Only stick to bottom if user was already near the bottom, adjust if needed
        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        const isNearBottom = distanceFromBottom < 80;

        if (isNearBottom) {
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [history, liveMessages, isLoading]);

    //Header
    const chatHeaderName = group ? group.name || `Group #${group.id}` : recipient?.name;

    return (
        <Box display="flex" flexDirection="column" height="100%">
            <DrawerHeader name={chatHeaderName} onClose={onClose} />

            {group && group.members && (
                <Box px={2} py={1} borderBottom="1px solid #eee" display="flex" alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>Members:</Typography>
                    {group.members.map((member: any, idx: number) => (
                        <Box key={member.id || member} sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: 14, mr: 0.5 }}>
                                {getUserName(member.userId || member)[0]}
                            </Avatar>
                            <Typography variant="body2">{getUserName(member.userId || member)}</Typography>
                            {idx < group.members.length - 1 && <span style={{ margin: '0 4px', color: '#bbb' }}>|</span>}
                        </Box>
                    ))}
                </Box>
            )}

            <List ref={listRef} sx={{ flex: 1, overflowY: 'auto', px: 2 }}>
                {hasMore && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                        <Button
                            variant="text"
                            onClick={loadOlderMessages}
                            disabled={isLoading}
                            startIcon={<Icon>refresh</Icon>}
                        >
                            {isLoading ? 'Loading�' : 'Load older messages'}
                        </Button>
                    </Box>
                )}
                {allMessages.map((msg, idx) => (
                    <ListItem key={idx}>
                        <ListItemText
                            primary={
                                <>
                                    <strong>
                                        {getUserName(msg.fromUserId)}
                                    </strong>
                                    <span style={{ marginLeft: 12, color: '#888', fontSize: 12 }}>
                                        {msg.timestamp && formatTime(msg.timestamp)}
                                    </span>
                                </>
                            }
                            secondary={msg.message}
                            sx={{
                                textAlign: msg.fromUserId === currentUser.id ? 'right' : 'left',
                            }}
                        />
                    </ListItem>
                ))}
                <div ref={bottomRef} />
            </List>
            <Paper
                sx={{ display: 'flex', alignItems: 'center', p: 1 }}
                component="form"
                onSubmit={(e) => {
                    e.preventDefault();
                    sendMessage();
                }}
            >
                {/* Emoji button */}
                <IconButton onClick={() => {
                    setShowEmoji(v => !v);
                    if (inputRef.current) inputRef.current.focus();
                }}>
                    <Icon>insert_emoticon</Icon>
                </IconButton>

                {/* Emoji picker (TS types included) */}
                {showEmoji && (
                    <Box
                        sx={{ position: 'absolute', bottom: 56, left: 8, zIndex: 2000, boxShadow: 3 }}
                    >
                        <EmojiPicker
                            onEmojiClick={(emojiData) => {
                                //emojiData.emoji is the unicode char
                                setInput(prev => (prev || '') + emojiData.emoji);
                                setShowEmoji(false);
                                inputRef.current?.focus();
                            }}
                            autoFocusSearch={false}
                        />
                    </Box>
                )}

                <TextField
                    inputRef={inputRef}
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type a message..."
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            sendMessage();
                        }
                    }}
                />
                <IconButton type="submit" color="primary">
                    <Icon>send</Icon>
                </IconButton>
            </Paper>
        </Box>
    );
};

